import { useState } from "react";
import { api, type AssistantPreview, type AssistantReply } from "../../api.ts";
import { tokens, useTheme } from "../../glassTheme.tsx";
import { GlassButton } from "../glass/Button.tsx";

/**
 * The evening check-in field and the weekly reflection field share this: one
 * text field, submit, and — per §1's "assistant's write pattern" — whatever
 * it wrote shown as rows with an undo, not silently applied.
 *
 * There is no `evening_checkin` or `weekly_session` REST route (session 18
 * changes no backend contract) — both of those are MCP-tool-only names, a
 * thin orchestration layer over `/log`, `PATCH /tasks/:id` and
 * `POST /sessions` for a chat session, with no reply text of its own. This
 * runs the same primitives through the existing `/assistant/chat` endpoint
 * instead: `preamble` frames the typed text so the model knows to log/update
 * what changed and reply in under four lines, and every write still goes
 * through the same propose-then-confirm gate the floating Assistant uses —
 * nothing here writes without being shown first.
 */
export function SessionWrite({
  kind, placeholder, preamble, prompt, onWritten,
}: {
  kind: "evening" | "weekly";
  placeholder: string;
  preamble: string;
  /** The weekly reflection's one prompt line (§1); absent for evening. */
  prompt?: string;
  onWritten: () => void;
}) {
  const { colors } = useTheme();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState<string | null>(null);
  const [applied, setApplied] = useState<{ did: string[]; batch?: string; undone?: boolean } | null>(null);
  const [pending, setPending] = useState<{ id: string; wire: unknown[]; previews: AssistantPreview[] } | null>(null);

  const finish = async (r: AssistantReply, transcript: string) => {
    setReply(r.reply || "Recorded, nothing to add.");
    if (r.applied) setApplied({ did: r.applied.did, batch: r.applied.undoable ? r.applied.id : undefined });
    try { await api.recordSession({ kind, transcript, wrote: r.applied ? [{ did: r.applied.did }] : [] }); }
    catch { /* the sitting is still shown; only the record of it failed to save */ }
    onWritten();
  };

  const submitEmpty = async () => {
    // Reporting nothing is a valid evening (and a valid week) — the field can
    // be left empty and the screen does not comment (§1).
    try { await api.recordSession({ kind, transcript: null, wrote: [] }); } catch { /* best-effort */ }
    onWritten();
  };

  const submit = async () => {
    const said = text.trim();
    if (!said) return submitEmpty();
    setBusy(true); setError(null);
    try {
      const r = await api.assistantChat({ messages: [{ role: "user", content: preamble + said }] });
      if (r.pending) setPending({ id: r.pending.proposal_id, wire: r.messages, previews: r.pending.previews });
      else await finish(r, said);
    } catch (e: any) { setError(e?.message ?? "that did not go through"); }
    finally { setBusy(false); }
  };

  const answer = async (yes: boolean) => {
    if (!pending) return;
    setBusy(true); setError(null);
    try {
      const body = { proposal_id: pending.id, messages: pending.wire };
      const r = yes ? await api.assistantConfirm(body) : await api.assistantDecline(body);
      setPending(null);
      await finish(r, text.trim());
    } catch (e: any) { setError(e?.message ?? "that did not go through"); }
    finally { setBusy(false); }
  };

  const undo = async () => {
    if (!applied?.batch || busy) return;
    setBusy(true);
    try { await api.assistantUndo(applied.batch); setApplied((a) => a && { ...a, undone: true, batch: undefined }); }
    catch (e: any) { setError(e?.message ?? "that did not go through"); }
    finally { setBusy(false); }
  };

  if (reply !== null) {
    return (
      <div>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: colors.ink }}>{reply}</p>
        {applied && applied.did.length > 0 && (
          <div style={{ marginTop: 10, display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: colors.ink2 }}>
              {applied.undone ? "done, then put back" : applied.did.join(" · ")}
            </span>
            {applied.batch && !applied.undone && (
              <button
                onClick={undo} disabled={busy}
                style={{
                  fontFamily: tokens.font.sans, fontSize: 12.5, color: colors.ink2, background: "none",
                  border: "none", padding: 0, cursor: "pointer", textDecoration: "underline",
                  opacity: busy ? 0.4 : 1,
                }}
              >undo</button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {prompt && (
        <div style={{
          fontFamily: tokens.font.serif, fontStyle: "italic", fontSize: tokens.type.question.size,
          lineHeight: tokens.type.question.lineHeight, color: colors.ink, marginBottom: 10,
        }}>{prompt}</div>
      )}

      {pending ? (
        <div>
          <PreviewRows previews={pending.previews} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <GlassButton variant="primary" disabled={busy} onClick={() => answer(true)}>{busy ? "…" : "Do it"}</GlassButton>
            <GlassButton variant="ghost" disabled={busy} onClick={() => answer(false)}>No</GlassButton>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <textarea
              value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={placeholder}
              style={{
                flex: 1, resize: "none", fontFamily: tokens.font.sans, fontSize: 16, lineHeight: 1.4,
                padding: "10px 12px", borderRadius: 8, border: `1px solid ${colors.hair2}`,
                background: colors.sheet, color: colors.ink, boxSizing: "border-box",
              }}
            />
            {/* Structural placeholder — no speech-to-text wired yet this session. */}
            <button
              aria-label="voice" disabled
              style={{
                width: 40, height: 40, flex: "none", borderRadius: "50%", border: `1px solid ${colors.hair2}`,
                background: "none", color: colors.ink2, opacity: 0.5,
              }}
            >🎙</button>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <GlassButton variant="primary" disabled={busy} onClick={submit}>{busy ? "…" : "Submit"}</GlassButton>
          </div>
        </>
      )}
      {error && <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "#9C4A3C" }}>{error}</p>}
    </div>
  );
}

function PreviewRows({ previews }: { previews: AssistantPreview[] }) {
  const { colors } = useTheme();
  return (
    <>
      {previews.map((p, i) => (
        <div key={i} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 13.5, lineHeight: 1.4, color: colors.ink, marginBottom: 4 }}>{p.headline}</div>
          {p.lines.map((l, j) => (
            <div key={j} style={{ fontSize: 11.5, color: colors.ink2, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span style={{ minWidth: 80 }}>{l.field.replace(/_/g, " ")}</span>
              {l.from != null && <span style={{ textDecoration: "line-through" }}>{l.from}</span>}
              <span style={{ color: colors.ink }}>{l.to ?? "—"}</span>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}
